import { langOf, setLangSource, type Lang } from '@procumi/shared';

/**
 * Limba din browser (2026-10-29). `t()` din shared întreabă de aici limba și cuvântul
 * firmei pentru șantier.
 *
 * Constantele cu texte se calculează la încărcarea modulelor, înainte să știm cine
 * e logat; de aceea limba și cuvântul firmei se țin și în browser: la pornire le
 * luăm de acolo, iar dacă omul logat are altele, le scriem și reîncărcăm pagina o
 * singură dată (`syncLang`). Fără nimic salvat: limba browserului.
 */
const KEY = 'procumi.lang';

type Ctx = { lang: Lang; term: string | null; currency?: string | null; country?: string | null };

function stored(): Ctx | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { lang?: string; term?: string | null; currency?: string; country?: string };
    return {
      lang: langOf(v.lang),
      term: v.term ?? null,
      currency: v.currency ?? null,
      country: v.country ?? null,
    };
  } catch {
    return null;
  }
}

const browserLang = (): Lang => (/^ro\b/i.test(navigator.language || 'ro') ? 'ro' : 'en');

/** `?lang=en`: linkul de pe site-ul în engleză („Try it free”) deschide aplicația în engleză. */
const fromLink = (): Lang | null => {
  try {
    const v = new URLSearchParams(window.location.search).get('lang');
    return v ? langOf(v) : null;
  } catch {
    return null;
  }
};

let ctx: Ctx = stored() ?? { lang: browserLang(), term: null };
const linked = fromLink();
if (linked && linked !== ctx.lang) {
  ctx = { ...ctx, lang: linked };
  save();
}
document.documentElement.lang = ctx.lang;

setLangSource(() => ctx);

export const lang = (): Lang => ctx.lang;

function save(): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(ctx));
    return localStorage.getItem(KEY) === JSON.stringify(ctx);
  } catch {
    return false;
  }
}

/**
 * Limba aleasă înainte de login (ecranul de intrare, înscrierea): se schimbă pe loc,
 * cu reîncărcare, ca toate textele să fie în noua limbă.
 */
export function chooseLang(l: Lang): void {
  if (l === ctx.lang) return;
  ctx = { ...ctx, lang: l };
  save();
  window.location.reload();
}

/**
 * Omul logat și firma lui: dacă limba sau cuvântul pentru șantier diferă de ce
 * s-a încărcat, le ținem minte și reîncărcăm o dată. Fără loc de ținut minte
 * (browser privat), schimbăm doar pe loc: ecranele desenate de acum încolo ies
 * bine, constantele rămân în limba de la pornire.
 */
export function syncLang(l: Lang, term: string, currency?: string, country?: string): void {
  // moneda și țara firmei (sumele, TVA-ul) se schimbă pe loc, fără reîncărcare
  const money = { currency: currency ?? ctx.currency ?? null, country: country ?? ctx.country ?? null };
  if (money.currency !== ctx.currency || money.country !== ctx.country) {
    ctx = { ...ctx, ...money };
    save();
  }
  if (l === ctx.lang && term === ctx.term) return;
  ctx = { lang: l, term, ...money };
  document.documentElement.lang = l;
  if (save()) {
    // o singură reîncărcare pe sesiune, ca să nu se poată învârti
    const mark = `${l}|${term}`;
    try {
      if (sessionStorage.getItem(KEY) === mark) return;
      sessionStorage.setItem(KEY, mark);
    } catch {
      return;
    }
    window.location.reload();
  }
}
